// @ts-check
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import useReportFiltersContext from "../../../../../hooks/contextConsumers/useReportFiltersContext.hook"

import ClientColumnDataTable from "./ClientTasksReportTable.controller"
import { useExportTasksPerClient } from "../../../../../hooks/reports/useExportTasksPerClient"
import { useClientTasksReportData } from "./useClientTasksReportData.hook"
import useToast from "../../../../../hooks/useToast.hook"

const ClientTasksTableContainer = forwardRef(
  /**
   * @param {import("./ClientTasksTableContainer.controller.types").ClientTasksTableContainerProps} props
   * @param {React.Ref<import("./ClientTasksTableContainer.controller.types").ClientTasksTableContainerControllerRef>} ref
   */
  (props, ref) => {
    const {
      contextMenuRef,
      setContextMenuTarget,
      updatingTask,
      loading,
      setLoading,
    } = props

    const { setErrorMessage } = useToast()

    const {
      tasksByClientSelectedClient,
      tasksByClientDateRange,
      tasksByClientPeriod,
    } = useReportFiltersContext()

    /** @type {React.MutableRefObject<import("./ClientTasksReportTable.controller.types").ClientTasksReportTableControllerRef | null >} */
    const tableRef = useRef(null)

    const [filterValues, setFilterValues] = useState(
      /** @type {import("./ClientTasksReportTable.controller.types").ClientTasksReportTableFilterValues} */ ({})
    )

    const handleFilterValuesChange = useCallback(
      /** @param {import("./ClientTasksReportTable.controller.types").ClientTasksReportTableFilterValues} nextFilterValues */
      (nextFilterValues) => {
        setFilterValues(
          /** @param {import("./ClientTasksReportTable.controller.types").ClientTasksReportTableFilterValues} previousFilterValues */
          (previousFilterValues) => {
            const previousKey = JSON.stringify(previousFilterValues || {})
            const nextKey = JSON.stringify(nextFilterValues || {})

            return previousKey === nextKey ? previousFilterValues : (
                nextFilterValues
              )
          }
        )
      },
      []
    )

    const {
      tasks,
      totals,
      categories,
      modules,
      projects,
      teamMembers,
      isLoading,
      resetData,
      refreshData,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    } = useClientTasksReportData({
      clientId: tasksByClientSelectedClient,
      dateRange: tasksByClientDateRange,
      filterValues,
    })

    const infiniteScroll = useMemo(
      () => ({
        fetchNextPage,
        hasNextPage,
        fetchThreshold: 10,
        isFetchingNextPage,
      }),
      [fetchNextPage, hasNextPage, isFetchingNextPage]
    )

    useEffect(() => {
      setLoading?.(isLoading)
    }, [isLoading])

    const { exportTasks } = useExportTasksPerClient()

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          tableRef.current?.reset()
          resetData()
        },
        exportCSV: async () => {
          const { success, message } = await exportTasks({
            clientCode: tasksByClientSelectedClient,
            filterConfig: tableRef.current?.getFilterValues(),
            sortConfig: tableRef.current?.getSortingValue(),
            startDate: tasksByClientDateRange[0],
            endDate: tasksByClientDateRange[1],
            period: tasksByClientPeriod,
          })

          if (!success && message) {
            setErrorMessage({ message })
          }
        },
        refresh: refreshData,
      }),
      [
        resetData,
        exportTasks,
        tasksByClientSelectedClient,
        tasksByClientDateRange,
        tasksByClientPeriod,
        refreshData,
      ]
    )

    return (
      <ClientColumnDataTable
        ref={tableRef}
        value={tasks}
        totals={totals}
        contextMenuRef={contextMenuRef}
        setContextMenuTarget={setContextMenuTarget}
        updatingTask={updatingTask}
        infiniteScroll={infiniteScroll}
        categories={categories}
        modules={modules}
        projects={projects}
        teamMembers={teamMembers}
        loading={loading}
        onFilterValuesChange={handleFilterValuesChange}
      />
    )
  }
)

// For the devtools to not show `Anonymous`
ClientTasksTableContainer.displayName = "ClientTasksTableContainer"
export default ClientTasksTableContainer
